import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Text,
    TextInput,
    Textarea,
    Button,
    Paper,
    ActionIcon,
    Divider,
    Select,
    Badge,
    Alert,
    Tabs,
    Group,
    Stack
} from '@mantine/core';
import {
    IconPlus,
    IconTrash,
    IconArrowUp,
    IconArrowDown,
    IconAlertTriangle,
    IconCode,
    IconEyeOff
} from '@tabler/icons-react';
import { EntityTypeSelector } from '../common/EntityTypeSelector';
import { JsonViewer } from '../common/JsonViewer';

export interface ConfigValue {
    key: string;
    value: any;
    type: 'string' | 'number' | 'boolean' | 'object' | 'array';
    children?: ConfigValue[]; // For nested objects/arrays
}

export interface ConfigProp {
    name: string;
    controlType: string; // Allow any string to handle bad data from API
    defaultValue?: string | boolean | number;
    options?: string[]; // For dropdown
    entityType?: string; // For entitySelector and entitySelectorMulti
}

interface ConfigBuilderProps {
    value: string; // JSON string
    onChange: (jsonString: string) => void;
    onError?: (error: string | null) => void;
}

export const ConfigBuilder: React.FC<ConfigBuilderProps> = ({
    value,
    onChange,
    onError
}) => {
    const [configValues, setConfigValues] = useState<ConfigValue[]>([]);
    const [props, setProps] = useState<ConfigProp[]>([]);
    const [activeSection, setActiveSection] = useState('0'); // 0 = Props, 1 = Properties
    const [error, setError] = useState<string | null>(null);
    const [dropdownRawInputs, setDropdownRawInputs] = useState<{ [propIndex: number]: string }>({});
    const [showPreview, setShowPreview] = useState(false);

    // Standard control types that are known/valid
    const standardControlTypes = ['text', 'multiline', 'dropdown', 'checkbox', 'entitySelector', 'entitySelectorMulti'];

    // Collect all unique control types from props (including any bad data from API)
    const allControlTypes = useMemo(() => {
        const types = new Set<string>(standardControlTypes);
        props.forEach(prop => {
            if (prop.controlType) {
                types.add(prop.controlType);
            }
        });
        return Array.from(types).sort();
    }, [props]);

    // Check if a control type is unknown/non-standard
    const isUnknownControlType = (controlType: string): boolean => {
        return !standardControlTypes.includes(controlType);
    };

    // Parse JSON string to ConfigValue array
    const parseJsonToConfigValues = (jsonString: string): ConfigValue[] => {
        if (!jsonString || jsonString.trim() === '' || jsonString.trim() === '{}') {
            return [];
        }

        try {
            const obj = JSON.parse(jsonString);
            if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
                return [];
            }

            // Exclude 'props', 'editVersion', and 'editDate' from config values since they're handled separately
            return Object.entries(obj)
                .filter(([key]) => key !== 'props' && key !== 'editVersion' && key !== 'editDate' && key !== 'version' && key !== 'updatedAt')
                .map(([key, val]) => {
                    let type: ConfigValue['type'] = 'string';
                    let children: ConfigValue[] | undefined = undefined;

                    if (typeof val === 'number') {
                        type = 'number';
                    } else if (typeof val === 'boolean') {
                        type = 'boolean';
                    } else if (Array.isArray(val)) {
                        type = 'array';
                        children = val.map((item, index) => ({
                            key: String(index),
                            value: item,
                            type: typeof item === 'object' && item !== null ? 'object' : typeof item as 'string' | 'number' | 'boolean',
                            children: typeof item === 'object' && item !== null && !Array.isArray(item)
                                ? Object.entries(item).map(([k, v]) => ({
                                    key: k,
                                    value: v,
                                    type: typeof v === 'object' && v !== null ? 'object' : typeof v as 'string' | 'number' | 'boolean'
                                }))
                                : undefined
                        }));
                    } else if (typeof val === 'object' && val !== null) {
                        type = 'object';
                        children = Object.entries(val).map(([k, v]) => ({
                            key: k,
                            value: v,
                            type: typeof v === 'object' && v !== null && !Array.isArray(v) ? 'object' : typeof v as 'string' | 'number' | 'boolean'
                        }));
                    }

                    return {
                        key,
                        value: type === 'object' || type === 'array' ? '' : val,
                        type,
                        children
                    };
                });
        } catch (e) {
            setError(`Failed to parse JSON: ${e instanceof Error ? e.message : 'Unknown error'}`);
            if (onError) {
                onError(`Failed to parse JSON: ${e instanceof Error ? e.message : 'Unknown error'}`);
            }
            return [];
        }
    };

    // Parse props from JSON
    const parsePropsFromJson = (jsonString: string): ConfigProp[] => {
        if (!jsonString || jsonString.trim() === '' || jsonString.trim() === '{}') {
            return [];
        }

        try {
            const obj = JSON.parse(jsonString);
            if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
                return [];
            }

            const propsArray = obj.props;
            if (!Array.isArray(propsArray)) {
                return [];
            }

            return propsArray.map((prop: any) => ({
                name: prop.name || '',
                controlType: prop.controlType || 'text',
                defaultValue: prop.defaultValue,
                options: prop.options || (prop.controlType === 'dropdown' ? [] : undefined),
                entityType: prop.entityType || (prop.controlType && prop.controlType.includes('entitySelector') ? '' : undefined)
            }));
        } catch (e) {
            return [];
        }
    };

    // Strip insignificant whitespace from a string
    const stripWhitespace = (value: any): any => {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (Array.isArray(value)) {
            return value.map(item => stripWhitespace(item));
        }
        if (typeof value === 'object' && value !== null) {
            const stripped: any = {};
            for (const [key, val] of Object.entries(value)) {
                stripped[key] = stripWhitespace(val);
            }
            return stripped;
        }
        return value;
    };

    // Convert ConfigValue array to JSON string
    const configValuesToJson = (values: ConfigValue[], propsArray: ConfigProp[]): string => {
        const obj: any = {};

        // Add props if any exist
        if (propsArray.length > 0) {
            obj.props = propsArray.map(prop => {
                const propObj: any = {
                    name: prop.name ? prop.name.trim() : prop.name,
                    controlType: prop.controlType
                };
                if (prop.defaultValue !== undefined && prop.defaultValue !== null && prop.defaultValue !== '') {
                    // Strip whitespace from string default values
                    propObj.defaultValue = typeof prop.defaultValue === 'string'
                        ? prop.defaultValue.trim()
                        : prop.defaultValue;
                }
                if (prop.controlType === 'dropdown' && prop.options && prop.options.length > 0) {
                    // Strip whitespace from each option
                    propObj.options = prop.options.map(opt => opt.trim()).filter(opt => opt !== '');
                }
                if (prop.controlType.includes('entitySelector') && prop.entityType) {
                    propObj.entityType = prop.entityType.trim();
                }
                return propObj;
            });
        }

        values.forEach(item => {
            if (item.type === 'object' && item.children) {
                const nestedObj: any = {};
                item.children.forEach(child => {
                    nestedObj[child.key.trim()] = stripWhitespace(child.value);
                });
                obj[item.key.trim()] = nestedObj;
            } else if (item.type === 'array' && item.children) {
                obj[item.key.trim()] = item.children.map(child => {
                    if (child.type === 'object' && child.children) {
                        const nestedObj: any = {};
                        child.children.forEach(grandchild => {
                            nestedObj[grandchild.key.trim()] = stripWhitespace(grandchild.value);
                        });
                        return nestedObj;
                    }
                    return stripWhitespace(child.value);
                });
            } else {
                // Convert value based on type and strip whitespace
                if (item.type === 'number') {
                    obj[item.key.trim()] = Number(item.value);
                } else if (item.type === 'boolean') {
                    obj[item.key.trim()] = item.value === true || item.value === 'true';
                } else {
                    obj[item.key.trim()] = stripWhitespace(item.value);
                }
            }
        });

        return JSON.stringify(obj, null, 2);
    };

    const isInternalUpdate = useRef(false);
    const lastValueRef = useRef<string>(value);
    const isMountedRef = useRef(false);

    // Initialize from value prop on mount
    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            lastValueRef.current = value;
            if (value) {
                const parsed = parseJsonToConfigValues(value);
                const parsedProps = parsePropsFromJson(value);
                setConfigValues(parsed);
                setProps(parsedProps);
                // Initialize raw inputs for dropdown props
                const rawInputs: { [index: number]: string } = {};
                parsedProps.forEach((prop, index) => {
                    if (prop.controlType === 'dropdown' && prop.options && prop.options.length > 0) {
                        rawInputs[index] = prop.options.join('\n');
                    }
                });
                setDropdownRawInputs(rawInputs);
            } else {
                setConfigValues([]);
                setProps([]);
                setDropdownRawInputs({});
            }
            return;
        }

        // Handle external value changes
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            lastValueRef.current = value;
            return;
        }

        if (value !== lastValueRef.current) {
            lastValueRef.current = value;
            if (value) {
                const parsed = parseJsonToConfigValues(value);
                const parsedProps = parsePropsFromJson(value);
                setConfigValues(parsed);
                setProps(parsedProps);
                // Initialize raw inputs for dropdown props
                const rawInputs: { [index: number]: string } = {};
                parsedProps.forEach((prop, index) => {
                    if (prop.controlType === 'dropdown' && prop.options && prop.options.length > 0) {
                        rawInputs[index] = prop.options.join('\n');
                    }
                });
                setDropdownRawInputs(rawInputs);
            } else {
                setConfigValues([]);
                setProps([]);
                setDropdownRawInputs({});
            }
        }
    }, [value]);

    // Update JSON when configValues or props change
    useEffect(() => {
        try {
            const jsonString = configValuesToJson(configValues, props);
            // Only call onChange if the JSON actually changed to avoid loops
            if (jsonString !== lastValueRef.current) {
                isInternalUpdate.current = true;
                lastValueRef.current = jsonString;
                onChange(jsonString);
            }
            setError(null);
            if (onError) {
                onError(null);
            }
        } catch (e) {
            const errMsg = `Failed to generate JSON: ${e instanceof Error ? e.message : 'Unknown error'}`;
            setError(errMsg);
            if (onError) {
                onError(errMsg);
            }
        }
    }, [configValues, props]);

    const handleAddProperty = () => {
        setConfigValues([
            ...configValues,
            {
                key: '',
                value: '',
                type: 'string'
            }
        ]);
    };

    const handleRemoveProperty = (index: number) => {
        setConfigValues(configValues.filter((_, i) => i !== index));
    };

    const handleMoveProperty = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index > 0) {
            const newValues = [...configValues];
            [newValues[index - 1], newValues[index]] = [newValues[index], newValues[index - 1]];
            setConfigValues(newValues);
        } else if (direction === 'down' && index < configValues.length - 1) {
            const newValues = [...configValues];
            [newValues[index], newValues[index + 1]] = [newValues[index + 1], newValues[index]];
            setConfigValues(newValues);
        }
    };

    const handlePropertyChange = (index: number, field: 'key' | 'value' | 'type', newValue: any) => {
        const newValues = [...configValues];
        if (field === 'type') {
            // Reset value when type changes
            newValues[index] = {
                ...newValues[index],
                type: newValue,
                value: newValue === 'boolean' ? false : newValue === 'number' ? 0 : '',
                children: (newValue === 'object' || newValue === 'array') ? [] : undefined
            };
        } else {
            newValues[index] = {
                ...newValues[index],
                [field]: newValue
            };
        }
        setConfigValues(newValues);
    };

    const handleAddNestedProperty = (parentIndex: number) => {
        const newValues = [...configValues];
        if (!newValues[parentIndex].children) {
            newValues[parentIndex].children = [];
        }
        newValues[parentIndex].children!.push({
            key: '',
            value: '',
            type: 'string'
        });
        setConfigValues(newValues);
    };

    const handleRemoveNestedProperty = (parentIndex: number, childIndex: number) => {
        const newValues = [...configValues];
        if (newValues[parentIndex].children) {
            newValues[parentIndex].children = newValues[parentIndex].children!.filter((_, i) => i !== childIndex);
        }
        setConfigValues(newValues);
    };

    const handleNestedPropertyChange = (
        parentIndex: number,
        childIndex: number,
        field: 'key' | 'value' | 'type',
        newValue: any
    ) => {
        const newValues = [...configValues];
        if (newValues[parentIndex].children) {
            if (field === 'type') {
                newValues[parentIndex].children![childIndex] = {
                    ...newValues[parentIndex].children![childIndex],
                    type: newValue,
                    value: newValue === 'boolean' ? false : newValue === 'number' ? 0 : '',
                    children: undefined
                };
            } else {
                newValues[parentIndex].children![childIndex] = {
                    ...newValues[parentIndex].children![childIndex],
                    [field]: newValue
                };
            }
        }
        setConfigValues(newValues);
    };

    // Parse generated JSON string to object for JsonViewer
    const generatedJsonObj = useMemo(() => {
        try {
            const jsonString = configValuesToJson(configValues, props);
            return jsonString && jsonString.trim() !== '' && jsonString.trim() !== '{}'
                ? JSON.parse(jsonString)
                : {};
        } catch (e) {
            return {};
        }
    }, [configValues, props]);

    // Props management functions
    const handleAddProp = () => {
        setProps([
            ...props,
            {
                name: '',
                controlType: 'text',
                defaultValue: ''
            }
        ]);
    };

    const handleRemoveProp = (index: number) => {
        setProps(props.filter((_, i) => i !== index));
    };

    const handlePropChange = (index: number, field: keyof ConfigProp, newValue: any) => {
        const newProps = [...props];
        if (field === 'controlType') {
            // Reset fields when control type changes
            newProps[index] = {
                ...newProps[index],
                controlType: newValue,
                defaultValue: newValue === 'checkbox' ? false : (newValue && newValue.includes('entitySelector')) ? '' : '',
                options: newValue === 'dropdown' ? [] : undefined,
                entityType: (newValue && newValue.includes('entitySelector')) ? '' : undefined
            };
            // Clear raw input when control type changes away from dropdown
            if (newValue !== 'dropdown') {
                setDropdownRawInputs(prev => {
                    const updated = { ...prev };
                    delete updated[index];
                    return updated;
                });
            }
        } else {
            newProps[index] = {
                ...newProps[index],
                [field]: newValue
            };
            // If options changed externally (not from raw input), update raw input
            if (field === 'options' && newValue && Array.isArray(newValue)) {
                setDropdownRawInputs(prev => ({
                    ...prev,
                    [index]: newValue.join('\n')
                }));
            }
        }
        setProps(newProps);
    };

    const handleAddDropdownOption = (propIndex: number) => {
        const newProps = [...props];
        if (!newProps[propIndex].options) {
            newProps[propIndex].options = [];
        }
        newProps[propIndex].options!.push('');
        setProps(newProps);
        // Update raw input to reflect the new option
        setDropdownRawInputs(prev => ({
            ...prev,
            [propIndex]: newProps[propIndex].options!.join('\n')
        }));
    };

    const handleRemoveDropdownOption = (propIndex: number, optionIndex: number) => {
        const newProps = [...props];
        if (newProps[propIndex].options) {
            newProps[propIndex].options = newProps[propIndex].options!.filter((_, i) => i !== optionIndex);
        }
        setProps(newProps);
        // Update raw input to reflect the removal
        if (newProps[propIndex].options) {
            setDropdownRawInputs(prev => ({
                ...prev,
                [propIndex]: newProps[propIndex].options!.join('\n')
            }));
        } else {
            // Clear raw input if no options left
            setDropdownRawInputs(prev => {
                const updated = { ...prev };
                delete updated[propIndex];
                return updated;
            });
        }
    };

    const handleDropdownOptionChange = (propIndex: number, optionIndex: number, value: string) => {
        const newProps = [...props];
        if (newProps[propIndex].options) {
            newProps[propIndex].options![optionIndex] = value;
        }
        setProps(newProps);
        // Update raw input to reflect the change
        if (newProps[propIndex].options) {
            setDropdownRawInputs(prev => ({
                ...prev,
                [propIndex]: newProps[propIndex].options!.join('\n')
            }));
        }
    };

    return (
        <Box>
            {error && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Group justify="space-between" align="center" mb="md">
                <Text size="sm" fw={600}>
                    Prop Values Builder
                </Text>
            </Group>

            <Tabs value={activeSection} onChange={(v) => setActiveSection(v ?? '0')} mb="md">
                <Tabs.List>
                    <Tabs.Tab value="0">Form Props</Tabs.Tab>
                    <Tabs.Tab value="1">Implicit Values</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="0">
                <Box>
                    <Group justify="space-between" align="center" mb="md">
                        <Text size="sm" c="dimmed">
                            Manual kick-off inputs: shown when this step runs; can be overridden by a workflow that imports this step.
                        </Text>
                        <Button
                            variant="outline"
                            size="sm"
                            leftSection={<IconPlus size={16} />}
                            onClick={handleAddProp}
                        >
                            Add Prop
                        </Button>
                    </Group>

                    {props.length === 0 ? (
                        <Paper p="lg" ta="center" withBorder>
                            <Text size="sm" c="dimmed">
                                No props defined. Click "Add Prop" to get started.
                            </Text>
                        </Paper>
                    ) : (
                        <Box>
                            {props.map((prop, index) => (
                                <Paper key={index} p="md" mb="md" withBorder>
                                    <Group gap="xs" align="flex-start" wrap="wrap">
                                        <TextInput
                                            label="Property Name"
                                            value={prop.name}
                                            onChange={(e) => {
                                                const newValue = e.currentTarget.value;
                                                if (newValue === '' || /^[a-zA-Z_]/.test(newValue)) {
                                                    handlePropChange(index, 'name', newValue);
                                                }
                                            }}
                                            size="sm"
                                            flex={1}
                                            miw={200}
                                            placeholder="e.g., title, description"
                                            error={prop.name !== '' && !/^[a-zA-Z_]/.test(prop.name)}
                                            description={prop.name !== '' && !/^[a-zA-Z_]/.test(prop.name) ? 'Property name must start with a letter or underscore' : undefined}
                                        />
                                        <Select
                                            label="Control Type"
                                            value={prop.controlType}
                                            onChange={(v) => handlePropChange(index, 'controlType', v ?? '')}
                                            data={allControlTypes.map(ct => ({ value: ct, label: ct + (isUnknownControlType(ct) ? ' ⚠️' : '') }))}
                                            size="sm"
                                            miw={180}
                                            error={isUnknownControlType(prop.controlType)}
                                        />
                                        {isUnknownControlType(prop.controlType) && (
                                            <Alert
                                                color="yellow"
                                                icon={<IconAlertTriangle size={16} />}
                                                mt="xs"
                                                w="100%"
                                            >
                                                Unknown control type: <strong>{prop.controlType}</strong>. This may not work as expected. Consider changing it to a standard type.
                                            </Alert>
                                        )}
                                        {prop.controlType !== 'checkbox' && !prop.controlType.includes('entitySelector') && (
                                            prop.controlType === 'multiline' ? (
                                                <Textarea
                                                    label="Default Value"
                                                    value={typeof prop.defaultValue === 'string' ? prop.defaultValue : (prop.defaultValue != null ? String(prop.defaultValue) : '')}
                                                    onChange={(e) => handlePropChange(index, 'defaultValue', e.currentTarget.value)}
                                                    size="sm"
                                                    flex={1}
                                                    miw={200}
                                                    placeholder="Optional"
                                                    rows={3}
                                                />
                                            ) : (
                                                <TextInput
                                                    label="Default Value"
                                                    value={typeof prop.defaultValue === 'string' ? prop.defaultValue : (prop.defaultValue != null ? String(prop.defaultValue) : '')}
                                                    onChange={(e) => handlePropChange(index, 'defaultValue', e.currentTarget.value)}
                                                    size="sm"
                                                    flex={1}
                                                    miw={200}
                                                    placeholder="Optional"
                                                />
                                            )
                                        )}
                                        {prop.controlType === 'checkbox' && (
                                            <Select
                                                label="Default Value"
                                                value={prop.defaultValue === true ? 'true' : 'false'}
                                                onChange={(v) => handlePropChange(index, 'defaultValue', v === 'true')}
                                                data={[
                                                    { value: 'true', label: 'True' },
                                                    { value: 'false', label: 'False' }
                                                ]}
                                                size="sm"
                                                miw={150}
                                            />
                                        )}
                                        {prop.controlType.includes('entitySelector') && (
                                            <Box flex={1} miw={200}>
                                                <EntityTypeSelector
                                                    value={prop.entityType || null}
                                                    onChange={(value) => handlePropChange(index, 'entityType', value || '')}
                                                    label="Entity Type"
                                                    allowClear={false}
                                                />
                                            </Box>
                                        )}
                                        <ActionIcon
                                            size="sm"
                                            color="red"
                                            variant="subtle"
                                            onClick={() => handleRemoveProp(index)}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>

                                    {prop.controlType === 'dropdown' && prop.options && prop.options.length > 0 &&
                                        prop.defaultValue !== undefined && prop.defaultValue !== null && String(prop.defaultValue).trim() !== '' &&
                                        !prop.options.some((opt: string) => String(opt).trim() === String(prop.defaultValue).trim()) && (
                                        <Alert color="yellow" mt="xs">
                                            Default value &quot;<strong>{String(prop.defaultValue).trim()}</strong>&quot; is not one of the dropdown options.
                                        </Alert>
                                    )}

                                    {prop.controlType === 'dropdown' && (
                                        <Box mt="md" pl="md" style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
                                            <Group justify="space-between" align="center" mb="xs">
                                                <Text size="xs" c="dimmed">
                                                    Dropdown Options (one per line or comma-separated)
                                                </Text>
                                                <Button
                                                    size="sm"
                                                    leftSection={<IconPlus size={16} />}
                                                    onClick={() => handleAddDropdownOption(index)}
                                                >
                                                    Add Option
                                                </Button>
                                            </Group>
                                            <Textarea
                                                value={dropdownRawInputs[index] !== undefined ? dropdownRawInputs[index] : (prop.options ? prop.options.join('\n') : '')}
                                                onBlur={(e) => {
                                                    const input = e.currentTarget.value;
                                                    const lines = input.split('\n');
                                                    const options: string[] = [];
                                                    lines.forEach(line => {
                                                        if (line.includes(',')) {
                                                            line.split(',').forEach(opt => {
                                                                const trimmed = opt.trim();
                                                                if (trimmed !== '') options.push(trimmed);
                                                            });
                                                        } else {
                                                            const trimmed = line.trim();
                                                            if (trimmed !== '') options.push(trimmed);
                                                        }
                                                    });
                                                    handlePropChange(index, 'options', options);
                                                    setDropdownRawInputs(prev => ({ ...prev, [index]: options.join('\n') }));
                                                }}
                                                onChange={(e) => {
                                                    setDropdownRawInputs(prev => ({ ...prev, [index]: e.currentTarget.value }));
                                                }}
                                                placeholder="Enter options, one per line or comma-separated"
                                                size="sm"
                                                mb="md"
                                                rows={4}
                                                description="You can also add options individually using the 'Add Option' button. Press Tab or click outside to parse."
                                            />
                                            {prop.options && prop.options.length > 0 && (
                                                <Box>
                                                    <Text size="xs" c="dimmed" mb="xs" component="span" display="block">
                                                        Individual Options:
                                                    </Text>
                                                    {prop.options.map((option, optionIndex) => (
                                                        <Group key={optionIndex} gap="xs" mb="xs" align="center">
                                                            <TextInput
                                                                label={`Option ${optionIndex + 1}`}
                                                                value={option}
                                                                onChange={(e) => handleDropdownOptionChange(index, optionIndex, e.currentTarget.value)}
                                                                size="sm"
                                                                flex={1}
                                                            />
                                                            <ActionIcon
                                                                size="sm"
                                                                color="red"
                                                                variant="subtle"
                                                                onClick={() => handleRemoveDropdownOption(index, optionIndex)}
                                                            >
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Group>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            ))}
                            <Group justify="flex-end" mt="md">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={handleAddProp}
                                >
                                    Add Prop
                                </Button>
                            </Group>
                        </Box>
                    )}
                </Box>
                </Tabs.Panel>

                <Tabs.Panel value="1">
                <Box>
                    <Group justify="space-between" align="center" mb="md">
                        <Text size="sm" c="dimmed">
                            Configurable values not settable at trigger time; can be overridden by a workflow but not when the step is kicked off.
                        </Text>
                        <Button
                            variant="outline"
                            size="sm"
                            leftSection={<IconPlus size={16} />}
                            onClick={handleAddProperty}
                        >
                            Add Implicit Value
                        </Button>
                    </Group>

                    {configValues.length === 0 ? (
                        <Paper p="lg" ta="center" withBorder>
                            <Text size="sm" c="dimmed">
                                No implicit values. Click "Add Implicit Value" to get started.
                            </Text>
                        </Paper>
                    ) : (
                        <Box>
                            {configValues.map((item, index) => (
                                <Paper key={index} p="md" mb="md" withBorder>
                                    <Group gap="xs" align="flex-start" mb={item.children && item.children.length > 0 ? 'md' : 0}>
                                        <TextInput
                                            label="Key"
                                            value={item.key}
                                            onChange={(e) => handlePropertyChange(index, 'key', e.currentTarget.value)}
                                            size="sm"
                                            flex={1}
                                        />
                                        <Select
                                            label="Type"
                                            value={item.type}
                                            onChange={(v) => handlePropertyChange(index, 'type', v ?? 'string')}
                                            data={[
                                                { value: 'string', label: 'String' },
                                                { value: 'number', label: 'Number' },
                                                { value: 'boolean', label: 'Boolean' },
                                                { value: 'object', label: 'Object' },
                                                { value: 'array', label: 'Array' }
                                            ]}
                                            size="sm"
                                            miw={120}
                                        />
                                        {item.type !== 'object' && item.type !== 'array' && item.type !== 'boolean' && (
                                            <TextInput
                                                label="Value"
                                                value={item.value}
                                                onChange={(e) => {
                                                    const val = item.type === 'number'
                                                        ? (e.currentTarget.value === '' ? 0 : Number(e.currentTarget.value))
                                                        : e.currentTarget.value;
                                                    handlePropertyChange(index, 'value', val);
                                                }}
                                                type={item.type === 'number' ? 'number' : 'text'}
                                                size="sm"
                                                flex={1}
                                            />
                                        )}
                                        {item.type === 'boolean' && (
                                            <Select
                                                label="Value"
                                                value={item.value === true || item.value === 'true' ? 'true' : 'false'}
                                                onChange={(v) => handlePropertyChange(index, 'value', v === 'true')}
                                                data={[
                                                    { value: 'true', label: 'True' },
                                                    { value: 'false', label: 'False' }
                                                ]}
                                                size="sm"
                                                miw={100}
                                            />
                                        )}
                                        <Stack gap="xs">
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                onClick={() => handleMoveProperty(index, 'up')}
                                                disabled={index === 0}
                                            >
                                                <IconArrowUp size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                onClick={() => handleMoveProperty(index, 'down')}
                                                disabled={index === configValues.length - 1}
                                            >
                                                <IconArrowDown size={16} />
                                            </ActionIcon>
                                        </Stack>
                                        <ActionIcon
                                            size="sm"
                                            color="red"
                                            variant="subtle"
                                            onClick={() => handleRemoveProperty(index)}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Group>

                                    {(item.type === 'object' || item.type === 'array') && (
                                        <Box ml="md" mt="md" pl="md" style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
                                            <Group justify="space-between" align="center" mb="xs">
                                                <Text size="xs" c="dimmed">
                                                    {item.type === 'object' ? 'Nested Properties' : 'Array Items'}
                                                </Text>
                                                <Button
                                                    size="sm"
                                                    leftSection={<IconPlus size={16} />}
                                                    onClick={() => handleAddNestedProperty(index)}
                                                >
                                                    Add {item.type === 'object' ? 'Property' : 'Item'}
                                                </Button>
                                            </Group>
                                            {item.children && item.children.length > 0 && (
                                                <Box>
                                                    {item.children.map((child, childIndex) => (
                                                        <Group key={childIndex} gap="xs" mb="xs" align="center">
                                                            {item.type === 'array' && (
                                                                <Badge size="sm" miw={40}>{childIndex}</Badge>
                                                            )}
                                                            <TextInput
                                                                label={item.type === 'array' ? 'Item' : 'Key'}
                                                                value={child.key}
                                                                onChange={(e) => handleNestedPropertyChange(index, childIndex, 'key', e.currentTarget.value)}
                                                                size="sm"
                                                                flex={1}
                                                                disabled={item.type === 'array'}
                                                            />
                                                            <Select
                                                                label="Type"
                                                                value={child.type}
                                                                onChange={(v) => handleNestedPropertyChange(index, childIndex, 'type', v ?? 'string')}
                                                                data={[
                                                                    { value: 'string', label: 'String' },
                                                                    { value: 'number', label: 'Number' },
                                                                    { value: 'boolean', label: 'Boolean' }
                                                                ]}
                                                                size="sm"
                                                                miw={100}
                                                            />
                                                            <TextInput
                                                                label="Value"
                                                                value={child.value}
                                                                onChange={(e) => {
                                                                    const val = child.type === 'number'
                                                                        ? (e.currentTarget.value === '' ? 0 : Number(e.currentTarget.value))
                                                                        : e.currentTarget.value;
                                                                    handleNestedPropertyChange(index, childIndex, 'value', val);
                                                                }}
                                                                type={child.type === 'number' ? 'number' : 'text'}
                                                                size="sm"
                                                                flex={1}
                                                            />
                                                            <ActionIcon
                                                                size="sm"
                                                                color="red"
                                                                variant="subtle"
                                                                onClick={() => handleRemoveNestedProperty(index, childIndex)}
                                                            >
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Group>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Box>
                </Tabs.Panel>
            </Tabs>

            <Divider my="lg" />

            <Box mt="md">
                <Button
                    variant="subtle"
                    size="sm"
                    leftSection={showPreview ? <IconEyeOff size={16} /> : <IconCode size={16} />}
                    onClick={() => setShowPreview(prev => !prev)}
                >
                    {showPreview ? 'Hide JSON Preview' : 'Preview JSON'}
                </Button>
                {showPreview && (
                    <Box mt="sm">
                        <JsonViewer
                            data={generatedJsonObj}
                            title="Settings JSON Preview"
                            description="This preview shows the Settings property that will be saved for this workflow activity."
                            formatBodyMultiline
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

